import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd"
import {
 IApVAlueInformation,
 ICommonInformation,
 IPTData,
 ISelectedDeviceIds,
 ISignalGroup,
 ISignalPlanDetector,
 ISignalPlanVisual,
} from "@types"
import { GridLine } from "components"
import React, { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useLocation } from "react-router-dom"
import { useFetchSignalPlanVisualMissingDataMutation } from "store/Features/processData/missionData-api-slice"
import { useFetchSignalPlanVisualMutation } from "../../store/Features/processData/signalPlanVisual-api-slice"

import { Button, OutSideClickHandler, SitePlanColorPicker } from "components"
import { useReactToPrint } from "react-to-print"
import { Tooltip } from "react-tooltip"
import "react-tooltip/dist/react-tooltip.css"
// import cookie from "react-cookies"

type TCombinationOfAll =
 | IMinutes[]
 | ISignalGroupTransformed[]
 | ISignalPlanTransformed[]
 | IDetectorTransformed[]

interface ICommonTransform {
 spId: string

 timestamp: string
 common_information: ICommonInformation

 ap_value_information: IApVAlueInformation

 pt_data: IPTData[]
}
interface ISignalPlanVisualTransformed {
 dates: string[]
 minutesSignalGroupDetectorsTXValue: TCombinationOfAll[]
}
interface ISignalGroupTransformed extends ICommonTransform, ISignalGroup {
 type: "signalGroup"
}

interface ISignalPlanTransformed extends ICommonTransform {
 type: "signalPlan"
}

interface IDetectorTransformed extends ICommonTransform, ISignalPlanDetector {
 type: "detector"
}

interface IMinutes extends ICommonTransform {
 type: "min"
}

interface IMissingDate {
 beginDate: string | null
 endTime: string | null
}
export const SitePlanDevices: React.FC = () => {
 const location = useLocation()

 const queryParams = new URLSearchParams(location.search)
 const beginDate = queryParams.get("begin_date")
 const endTime = queryParams.get("end_time")

 const [fetchSignalPlanVisual] = useFetchSignalPlanVisualMutation()
 const [fetchMissingSignalPlanVisual] = useFetchSignalPlanVisualMissingDataMutation()

 const selectedDevice: ISelectedDeviceIds = useSelector((state: any) => state.selectedDevicesIds)
 const [allData, setAllData] = useState<ISignalPlanVisualTransformed>()
 const containerRef = useRef<HTMLDivElement>(null)
 const [hoverIndex, setHoverIndex] = useState<number | null>(null)
 const [informationData, setInformationData] = useState<any | null>()
 const [minutesSelect, setMinutesSelect] = useState<number>(60)
 const [borderColor, setBorderColor] = useState("#000000")
 const [cursorColor, setCursorColor] = useState("#CD3616")
 const [bodyColor, setBodyColor] = useState("#f1f1f1")
 const [isShown, setIsShown] = useState<boolean>(false)
 const [showLiveData, setShowLiveData] = useState(true)
 const [isScroll, setIsScroll] = useState(true)
 const [isDragging, setIsDragging] = useState(false)
 const [startX, setStartX] = useState(0)
 const [scrollStart, setScrollStart] = useState(0)
 const [missingDates, setMissingDates] = useState<IMissingDate>({
  beginDate: null,
  endTime: null,
 })

 useEffect(() => {
  // Correctly pass an object with beginDate and endTime to the query function
  fetchSignalPlanVisual({
   beginDate: beginDate as string,
   endTime: endTime as string,
   deviceIds: selectedDevice.deviceIds as number[],
  })
   .then((res: any) => {
    if (res?.data) {
     const allResponses: TCombinationOfAll[] = res?.data.map((obj: any) => restructureHandler(obj))

     // Accumulate merged results
     let accumulatedMergedResults = { ...allData }

     allResponses.forEach((obj) => {
      const mergedResults = mergeNewData(accumulatedMergedResults, obj)
      accumulatedMergedResults = { ...accumulatedMergedResults, ...mergedResults }
     })

     // Update state with all merged results
     setAllData((prevData: ISignalPlanVisualTransformed | undefined) => {
      // Initialize mergedData with prevData or an empty object if prevData is undefined
      const mergedData: ISignalPlanVisualTransformed = prevData || {
       dates: [],
       minutesSignalGroupDetectorsTXValue: [],
      }

      // Provide default value for dates and minutesSignalGroupDetectorsTXValue if they are undefined
      const updatedData: ISignalPlanVisualTransformed = {
       ...mergedData,
       ...accumulatedMergedResults,
      }

      return updatedData
     })
    }
   })
   .catch((e: any) => {
    console.log("e", e)
   })
 }, [beginDate, endTime, selectedDevice])

 const restructureHandler = (signalPlanData: ISignalPlanVisual): ISignalPlanVisualTransformed => {
  // Directly including the common_information in the output
  const signalGroupsWithAP: ISignalGroupTransformed[] = signalPlanData.signal_groups?.map(
   (group: ISignalGroup) => ({
    ...group,
    spId: signalPlanData.signal_plan,
    type: "signalGroup",
    timestamp: signalPlanData.timestamp,
    ap_value_information: signalPlanData.ap_value_information,
    common_information: signalPlanData.common_information, // Include the actual common information here
    pt_data: signalPlanData.pt_data,
   }),
  )

  const detectorsWithAP: IDetectorTransformed[] = signalPlanData.detectors?.map(
   (detector: ISignalPlanDetector) => ({
    ...detector,
    spId: signalPlanData.signal_plan,
    type: "detector",
    timestamp: signalPlanData.timestamp,
    ap_value_information: signalPlanData.ap_value_information,
    common_information: signalPlanData.common_information, // Include the actual common information here
    pt_data: signalPlanData.pt_data,
   }),
  )

  return {
   dates: [signalPlanData.timestamp],
   minutesSignalGroupDetectorsTXValue: [
    [
     {
      type: "signalPlan",
      spId: signalPlanData.signal_plan,
      timestamp: signalPlanData.timestamp,
      ap_value_information: signalPlanData.ap_value_information,
      common_information: signalPlanData.common_information, // Include the actual common information here
      pt_data: signalPlanData.pt_data,
     },
    ],
    [
     {
      spId: signalPlanData.signal_plan,
      type: "min",
      timestamp: signalPlanData.timestamp,
      ap_value_information: signalPlanData.ap_value_information,
      common_information: signalPlanData.common_information, // Include the actual common information here
      pt_data: signalPlanData.pt_data,
     },
    ],
    ...signalGroupsWithAP.map((group: any) => [group]),
    ...detectorsWithAP.map((detector: any) => [detector]),
   ],
  }
 }

 function mergeNewData(existingData: any, upcomingData: any) {
  if (!existingData || Object.keys(existingData).length === 0) {
   return upcomingData
  }
  const requiredData = { ...existingData }

  // Merge dates
  requiredData.dates.push(...upcomingData.dates)

  // Extract all ocit_outstation_numbers from existingData with type "signalGroup"
  const existingOcitNumbers = existingData.minutesSignalGroupDetectorsTXValue
   .filter((existingItem: any) => existingItem[0].type === "signalGroup")
   .map((existingItem: any) => existingItem[0].ocit_outstation_number)

  // Extract all ocit_outstation_numbers from upcomingData with type "signalGroup"
  const upcomingOcitNumbers = upcomingData.minutesSignalGroupDetectorsTXValue
   .filter((upcomingItem: any) => upcomingItem[0].type === "signalGroup")
   .map((upcomingItem: any) => upcomingItem[0].ocit_outstation_number)

  // Find ocit_outstation_numbers that are present in existingData but not in upcomingData
  const missingOcitNumbers = existingOcitNumbers.filter(
   (ocitNumber: any) => !upcomingOcitNumbers.includes(ocitNumber),
  )

  //Not in existing and present in upcomming signalGroup
  const missingOcitNumbersNotExistingInExitingOcitNumber = upcomingOcitNumbers.filter(
   (ocitNumber: any) => !existingOcitNumbers.includes(ocitNumber),
  )

  // Extract all ocit_outstation_numbers from existingData with type "detector"
  const existingDetectorOcitNumbers = existingData.minutesSignalGroupDetectorsTXValue
   .filter((existingItem: any) => existingItem[0].type === "detector")
   .map((existingItem: any) => existingItem[0].ocit_outstation_number)

  // Extract all ocit_outstation_numbers from upcomingData with type "detector"
  const upcomingDetectorOcitNumbers = upcomingData.minutesSignalGroupDetectorsTXValue
   .filter((upcomingItem: any) => upcomingItem[0].type === "detector")
   .map((upcomingItem: any) => upcomingItem[0].ocit_outstation_number)

  // Find ocit_outstation_numbers that are present in existingData but not in upcomingData
  const missingDetectorOcitNumbers = existingDetectorOcitNumbers.filter(
   (ocitNumber: any) => !upcomingDetectorOcitNumbers.includes(ocitNumber),
  )

  //Not in existing and present in upcomming signalGroup
  const missingDetectorOcitNumbersNotInExistingData = upcomingDetectorOcitNumbers.filter(
   (ocitNumber: any) => !existingDetectorOcitNumbers.includes(ocitNumber),
  )
  let maxLength = 0
  let maxLengthIndex = 0

  existingData.minutesSignalGroupDetectorsTXValue.forEach(
   (subArray: any, requiredIndex: number) => {
    if (subArray.length > maxLength) {
     maxLength = subArray.length
     maxLengthIndex = requiredIndex
    }
   },
  )
  // Merge minutesSignalGroupDetectorsTXValue
  upcomingData.minutesSignalGroupDetectorsTXValue.forEach((upcomingItem: any) => {
   let matched = false

   requiredData.minutesSignalGroupDetectorsTXValue.forEach((existingItem: any) => {
    // Check if types match
    if (existingItem[0].type === upcomingItem[0].type) {
     // Check if ocit_outstation_number matches
     if (existingItem[0].ocit_outstation_number === upcomingItem[0].ocit_outstation_number) {
      existingItem.push(...upcomingItem)
      matched = true
     }
    }
   })

   // If no match found, create a new array of objects
   if (!matched) {
    requiredData.minutesSignalGroupDetectorsTXValue.push(upcomingItem)
   }
  })

  // Push the existingItems with missing ocit_outstation_numbers into requiredData
  existingData.minutesSignalGroupDetectorsTXValue.forEach((existingItem: any) => {
   if (
    existingItem[0].type === "signalGroup" &&
    missingOcitNumbers.includes(existingItem[0].ocit_outstation_number)
   ) {
    // Find the index of the array based on ocit_outstation_number
    const indexPosition = requiredData.minutesSignalGroupDetectorsTXValue.findIndex((item: any) => {
     return item[0].ocit_outstation_number === existingItem[0].ocit_outstation_number
    })
    // if the index is found,  push the existingItem into the same array
    if (indexPosition !== -1) {
     //Create a copy of the existingItem

     const updatedItem = { ...existingItem[existingItem.length - 1] }

     updatedItem.timestamp = upcomingData.minutesSignalGroupDetectorsTXValue[0][0]?.timestamp

     updatedItem.ap_value_information =
      upcomingData.minutesSignalGroupDetectorsTXValue[0][0]?.ap_value_information
     //  updatedItem.signal_state = "transparent"
     requiredData.minutesSignalGroupDetectorsTXValue[indexPosition].push(updatedItem)
    }
   }
  })
  // Push the existingDetectorItems with missing ocit_outstation_numbers into requiredData

  existingData.minutesSignalGroupDetectorsTXValue.forEach((existingItem: any) => {
   if (
    existingItem[0].type === "detector" &&
    missingDetectorOcitNumbers.includes(existingItem[0].ocit_outstation_number)
   ) {
    // Find the index of the array based on ocit_outstation_number
    const indexPosition = requiredData.minutesSignalGroupDetectorsTXValue.findIndex((item: any) => {
     return item[0].ocit_outstation_number === existingItem[0].ocit_outstation_number
    })
    // if the index is found,  push the existingItem into the same array
    if (indexPosition !== -1) {
     //Create a copy of the existingItem

     const updatedItem = { ...existingItem[existingItem.length - 1] }

     updatedItem.timestamp = upcomingData.minutesSignalGroupDetectorsTXValue[0][0]?.timestamp

     updatedItem.ap_value_information =
      upcomingData.minutesSignalGroupDetectorsTXValue[0][0]?.ap_value_information

     requiredData.minutesSignalGroupDetectorsTXValue[indexPosition].push(updatedItem)
    }
   }
  })

  //Push the upcomingItems with missing ocit_outstation_numbers into requiredData
  upcomingData.minutesSignalGroupDetectorsTXValue.forEach((upcomingItem: any) => {
   if (
    upcomingItem[0].type === "signalGroup" &&
    missingOcitNumbersNotExistingInExitingOcitNumber.includes(
     upcomingItem[0].ocit_outstation_number,
    )
   ) {
    const indexPosition = requiredData.minutesSignalGroupDetectorsTXValue.findIndex((item: any) => {
     return item[0].ocit_outstation_number === upcomingItem[0].ocit_outstation_number
    })

    // if the index is found,  push the existingItem into the same array
    if (indexPosition !== -1) {
     //Create a copy of the existingItem
     // let updatedItem = { ...upcomingItem[0] }
     const itemsToPush = []

     // Push updatedItem into maxLength times
     for (let i = 0; i < maxLength; i++) {
      itemsToPush.push({
       ...upcomingItem[0],
       spId: existingData.minutesSignalGroupDetectorsTXValue[maxLengthIndex][i].spId,

       timestamp: existingData.minutesSignalGroupDetectorsTXValue[maxLengthIndex][i].timestamp,
       common_information:
        existingData.minutesSignalGroupDetectorsTXValue[maxLengthIndex][i].common_information,

       ap_value_information:
        existingData.minutesSignalGroupDetectorsTXValue[maxLengthIndex][i].ap_value_information,

       pt_data: existingData.minutesSignalGroupDetectorsTXValue[maxLengthIndex][i].pt_data,
       signal_state: "transparent",
      })
     }

     // Push each item from itemsToPush into requiredData
     requiredData.minutesSignalGroupDetectorsTXValue[indexPosition] = [
      ...itemsToPush,
      ...requiredData.minutesSignalGroupDetectorsTXValue[indexPosition],
     ]
    }
   }
  })

  //Push the Detector upcomingItems with missing ocit_outstation_numbers into requiredData
  upcomingData.minutesSignalGroupDetectorsTXValue.forEach((upcomingItem: any) => {
   if (
    upcomingItem[0].type === "detector" &&
    missingDetectorOcitNumbersNotInExistingData.includes(upcomingItem[0].ocit_outstation_number)
   ) {
    const indexPosition = requiredData.minutesSignalGroupDetectorsTXValue.findIndex((item: any) => {
     return item[0].ocit_outstation_number === upcomingItem[0].ocit_outstation_number
    })

    // if the index is found,  push the existingItem into the same array
    if (indexPosition !== -1) {
     //Create a copy of the existingItem
     const itemsToPush = []

     // Push updatedItem into maxLength times
     for (let i = 0; i < maxLength; i++) {
      itemsToPush.push({
       ...upcomingItem[0],
       detector_triggered: null,
       spId: existingData.minutesSignalGroupDetectorsTXValue[maxLengthIndex][i].spId,

       timestamp: existingData.minutesSignalGroupDetectorsTXValue[maxLengthIndex][i].timestamp,
       common_information:
        existingData.minutesSignalGroupDetectorsTXValue[maxLengthIndex][i].common_information,

       ap_value_information:
        existingData.minutesSignalGroupDetectorsTXValue[maxLengthIndex][i].ap_value_information,

       pt_data: existingData.minutesSignalGroupDetectorsTXValue[maxLengthIndex][i].pt_data,
      })
     }

     // Push each item from itemsToPush into requiredData
     requiredData.minutesSignalGroupDetectorsTXValue[indexPosition] = [
      ...itemsToPush,
      ...requiredData.minutesSignalGroupDetectorsTXValue[indexPosition],
     ]
    }
   }
  })
  return requiredData
 }

 useEffect(() => {
  let socket: WebSocket | null = null // Initialize socket variable

  function connectSocket() {
   if (!socket) {
    // Token obtained from cookie
    // const token = cookie.load("token")

    //Hard-coded token
    const token =
     "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJuYW1lIjoid2Vic29ja2V0IiwicGFzc3dvcmQiOiJ0ZXN0In0.Qeibok6YCameSeRIL_nzmmkWLd0MHJrakNsw7wWcC5E" // Retrieve the token from wherever it's stored
    socket = new WebSocket(
     `${process.env.REACT_APP_WS_ENDPOINT}/ws/signal-plan-visual?authHeader=${token}`,
    )

    socket.onopen = function (event) {
     console.log("connected!!!", event)
     // Check if selectedDevice and device_id are available before sending the message

     sendMessage()
    }

    socket.onmessage = function (event) {
     // Handle received message
     const eventData = JSON.parse(event.data)
     // Transform and process data...
     if (eventData) {
      setAllData((prevEventData) => {
       const restructuredData = restructureHandler(eventData)
       return mergeNewData(prevEventData, restructuredData)
      })
     }
    }

    socket.onclose = function (event) {
     console.log("disconnected!!!", event)
     socket = null // Reset socket when closed
    }
   }
  }

  function sendMessage() {
   if (socket) {
    socket.send(
     JSON.stringify({
      action: "signal_plan_visual_setup",
      deviceId: selectedDevice.deviceIds,
     }),
    )
   }
  }

  if (showLiveData) {
   connectSocket() // Connect only if showLiveData is true
  } else {
   if (socket) {
    ;(socket as WebSocket).close() // Disconnect if showLiveData is false and socket exists
   }
  }

  return () => {
   if (socket) {
    ;(socket as WebSocket).close()
   }
  }
 }, [showLiveData])

 //  Effect for auto-scrolling
 useEffect(() => {
  if (containerRef.current && isScroll) {
   const { scrollWidth, clientWidth } = containerRef.current
   containerRef.current.scrollLeft = scrollWidth - clientWidth
  }
 }, [allData])

 useEffect(() => {
  if (missingDates.beginDate && missingDates.endTime) {
   // Check if both dates are non-null
   fetchMissingSignalPlanVisual({
    beginDate: missingDates.beginDate,
    endTime: missingDates.endTime,
   })
    .then((res: any) => {
     if (res?.data) {
      setAllData((currentData) => {
       const allResponses: TCombinationOfAll[] = res?.data.map((obj: any) =>
        restructureHandler(obj),
       )
       let restructuredDataAndMerge: ISignalPlanVisualTransformed | undefined = currentData
       allResponses.forEach((obj: any) => {
        restructuredDataAndMerge = mergeNewData(restructuredDataAndMerge, obj)
       })
       // Update state and set missingDates to empty values
       setMissingDates({ beginDate: "", endTime: "" })
       return restructuredDataAndMerge
      })
     }
    })
    .catch((e: any) => {
     console.log("e", e)
    })
  }
 }, [missingDates.beginDate, missingDates.endTime]) // Depend on beginDate and endTime
 // Function to determine color based on signal state
 const getColor = (signalState: string): string => {
  switch (signalState) {
   case "00000000":
    return "black"
   case "00001100":
    return "yellow"
   case "00110000":
    return "red"
   case "00000011":
    return "green"
   case "00111100":
    return "red-yellow"
   default:
    return "transparent"
  }
 }

 //  method for seconds change

 const processAllData = (item: any, index: number, element: any) => {
  switch (item.type) {
   case "min": {
    // Function to check if the current date is at least 5 seconds after the previous date
    const isDisplay = (currentDate: any, previousDate: any) => {
     const current: any = new Date(currentDate)
     const previous: any = new Date(previousDate)
     const diffInSeconds = (current - previous) / 1000
     return diffInSeconds >= 1
    }

    // Determine whether to display based on the comparison with the previous date/time
    const display = index === 0 || isDisplay(item.timestamp, element[index - 1]?.timestamp)

    const adjustedDifferences: any = []

    // Initialize the carry-over variable
    let carryOver = 0

    for (let index = 1; index < element.length; index++) {
     // Calculate the raw difference in seconds
     let differenceInSecondsRaw =
      (new Date(element[index].timestamp).getTime() -
       new Date(element[index - 1].timestamp).getTime()) /
      1000

     // Add any carry-over from the previous calculation
     differenceInSecondsRaw += carryOver

     // If the difference is greater than 1, set the difference for this interval to 1 and calculate the new carry-over
     if (differenceInSecondsRaw > 1) {
      carryOver = differenceInSecondsRaw - 1 // Set carry-over to the remainder
      adjustedDifferences.push(1) // Push "1" as the difference for this interval
     } else {
      carryOver = 0 // No carry-over for the next interval
      adjustedDifferences.push(differenceInSecondsRaw) // Push the actual difference if it's not greater than 1
     }
    }

    // `adjustedDifferences` now contains your adjusted differences with carry-over logic applied

    const showSecHandler = (dateTime: string, second: number) => {
     const sec: string = new Date(dateTime).toLocaleTimeString("en-GB", {
      second: "2-digit",
      timeZone: "UTC",
     })

     return Number(sec) % second === 0 ? sec : ""
    }

    return (
     <div key={index} className="flex text-sm items-end h-4">
      {display && (
       <div className="flex flex-col">
        <div className="text-right text-xs" style={{ width: adjustedDifferences * 24 }}>
         {showSecHandler(item.timestamp, 5)}
        </div>

        <div
         className={`border-1 h-1 opacity-50 w-6 ${index === 0 && "border-l"}`}
         style={{
          borderColor: borderColor,
          borderRightWidth: "1px",
          borderBottomWidth: "1px",
          borderLeftWidth: index === 0 ? "1px" : "",
         }}
        />
       </div>
      )}
     </div>
    )
   }
   case "signalPlan": {
    // Check if the current spId is different from the previous one
    const displaySPId = index === 0 || item.spId !== element[index - 1].spId ? item.spId : ""
    const displayEndedBorder =
     index === element.length - 1 || item.spId !== element[index + 1].spId ? true : false
    return (
     <div className="flex items-stretch bg-[#a0a0c0] border-transparent rounded">
      {/* Only display spId if it's different from the last one */}
      {displaySPId ? (
       <p
        className={`w-6 border-l-2 border-y-2 border-transparent  text-[10px] whitespace-nowrap flex items-center  bg-[#a0a0c0] ${
         displayEndedBorder && "border-r-2"
        }`}
       >
        {displaySPId}
       </p>
      ) : (
       <p
        className={`w-6 border-y-2 border-transparent bg-[#a0a0c0]   ${
         displayEndedBorder && "border-r-2"
        }`}
       ></p>
      )}
     </div>
    )
   }
   case "signalGroup": {
    const backgroundColor = getColor(item.signal_state)
    const isRedYellow = backgroundColor === "red-yellow"
    return (
     <div
      className="flex  tooltip-element w-full"
      onMouseEnter={() => {
       setHoverIndex(index)
       setInformationData(item.ap_value_information)
      }}
      onMouseLeave={() => {
       if (hoverIndex && hoverIndex === index) setHoverIndex(null)
       setInformationData(null)
      }}
     >
      <div className="flex flex-col">
       {isRedYellow ? (
        <>
         <p className="w-full h-2" style={{ backgroundColor: "red" }}></p>
         <p className="w-full h-2" style={{ backgroundColor: "yellow" }}></p>
        </>
       ) : (
        <p className="w-full h-4" style={{ backgroundColor: backgroundColor }}></p>
       )}
       <div
        className={`border-1 h-1 opacity-50 w-6 ${index === 0 && "border-l"}`}
        style={{
         borderColor: borderColor,
         borderRightWidth: "1px",
         borderBottomWidth: "1px",
         borderLeftWidth: index === 0 ? "1px" : "",
        }}
       />
      </div>
     </div>
    )
   }
   case "detector":
    return (
     <div
      className="flex tooltip-element w-full"
      onMouseEnter={() => {
       if (hoverIndex !== index) setHoverIndex(index)
       setInformationData(item.ap_value_information)
      }}
      onMouseLeave={() => {
       if (hoverIndex && hoverIndex === index) setHoverIndex(null)
       setInformationData(null)
      }}
     >
      <div className="flex flex-col">
       <p
        className={`w-full h-4 ${
         item.detector_triggered
          ? "bg-black"
          : item.detector_triggered === false
            ? "bg-white"
            : "bg-transparent"
        }`}
       >
        {" "}
        {item.detector_triggered}
       </p>{" "}
       <div
        className={`border-1 h-1 opacity-50 w-6 ${index === 0 && "border-l"}`}
        style={{
         borderColor: borderColor,
         borderRightWidth: "1px",
         borderBottomWidth: "1px",
         borderLeftWidth: index === 0 ? "1px" : "",
        }}
       />
      </div>
     </div>
    )
   default:
    return <></>
  }
 }

 const reorder = (list: TCombinationOfAll[], startIndex: number, endIndex: number) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
 }

 const onDragEnd = (result: any) => {
  // dropped outside the list
  if (!result.destination) {
   return
  }

  const _items: TCombinationOfAll[] = reorder(
   allData && allData?.minutesSignalGroupDetectorsTXValue
    ? [...allData.minutesSignalGroupDetectorsTXValue]
    : [],
   result.source.index,
   result.destination.index,
  )
  const _allData: ISignalPlanVisualTransformed | undefined = allData
  if (_allData) {
   _allData.minutesSignalGroupDetectorsTXValue = _items
   setAllData({ ..._allData })
  }
 }

 const calculateWidthHanlder = (data: any) => {
  const newData: any[] = [] // Initialize a new array to hold the processed data
  let previousGroupLength = 0 // Track the length of the previous group

  const largestLengthArray = data.reduce((prev: any, current: any) => {
   return prev.length > current.length ? prev : current
  }, [])

  data.forEach((group: any) => {
   const newGroup: any[] = [] // Initialize a new group array

   group.forEach((subItem: any, subIndex: number) => {
    // Always include the first item of each group in the newGroup
    if (subIndex === 0) {
     const newItem = { ...subItem }

     newItem.ap_value_information = {
      ...subItem.ap_value_information,
      entry_date: newItem.timestamp,
     }

     newGroup.push(newItem)
    } else {
     const previousItem = group[subIndex - 1]
     const previousTimestamp = new Date(previousItem.timestamp).getTime()
     const currentTimestamp = new Date(subItem.timestamp).getTime()
     const differenceInSeconds = (currentTimestamp - previousTimestamp) / 1000

     // Generate items based on the timestamp difference
     for (let i = 1; i <= differenceInSeconds; i++) {
      const incrementedTimestamp = new Date(previousTimestamp + i * 1000).toISOString()
      const newItem = { ...subItem }

      // let newItem = { ...subItem }
      newItem.timestamp = incrementedTimestamp
      newItem.ap_value_information = {
       ...subItem.ap_value_information,
       entry_date: incrementedTimestamp,
      }
      // Check to avoid adding a duplicate of the actual item
      if (i !== 1 || incrementedTimestamp !== subItem.timestamp) {
       newGroup.push(newItem)
      }
     }
    }
   })

   // After processing, ensure newGroup matches the length of the previous group
   if (previousGroupLength > 0) {
    // If newGroup is shorter, fill it with dummy entries
    while (newGroup.length < previousGroupLength) {
     const dummyItem = { ...newGroup[newGroup.length - 1] }

     dummyItem.ap_value_information = {
      ...newGroup[newGroup.length - 1].ap_value_information,
      entry_date: largestLengthArray[0].timestamp,
     }

     newGroup.push(dummyItem)
    }
    // Optionally, trim the newGroup if it's longer than the previous group
    // This is commented out as it might not be desired to lose data
    // while (newGroup.length > previousGroupLength) {
    //   newGroup.pop();
    // }
   }

   // Update previousGroupLength for the next iteration
   previousGroupLength = newGroup.length
   newData.push(newGroup) // Push the new group to the newData array
  })

  return newData // Return the new data structure
 }
 const componentRef = useRef(null)

 // method for printing the signalPlan
 const handlePrint = useReactToPrint({
  content: () => componentRef.current,
 })

 const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
  const valueAsNumber = Number(event.target.value)
  setMinutesSelect(valueAsNumber)
 }

 const options = [
  { value: "60", label: "1 minute" },
  { value: "180", label: "3 minute" },
  { value: "300", label: "5 minute" },
  { value: "480", label: "8 minute" },
  { value: "600", label: "10 minute" },
  { value: "1800", label: "30 minute" },
 ]

 const minuteHandler = (dateTime: string, secondsSelect: number) => {
  // Convert secondsSelect to minutes since it's named seconds but used as minutes
  const intervalMinutes = secondsSelect / 60
  const date = new Date(dateTime)
  const currentMinutes = date.getUTCMinutes() // Extract the minute part of the date

  // Now we can directly check if the currentMinutes is divisible by the intervalMinutes
  if (currentMinutes % intervalMinutes === 0) {
   // If divisible, return the time in the desired format
   return date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
   })
  } else {
   return ""
  }
 }

 const outSideClickHandler = () => {
  setIsShown(false)
 }

 const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
  if (containerRef.current) {
   // Checks if containerRef.current is not null
   setIsDragging(true)
   setStartX(e.clientX)
   setScrollStart(containerRef.current.scrollLeft)
  }
 }

 const onMouseMove = (e: MouseEvent) => {
  if (!isDragging) return
  e.preventDefault()
  if (containerRef.current !== null) {
   const x = e.clientX
   const walk = x - startX // Adjust the scroll speed if necessary
   containerRef.current.scrollLeft = scrollStart - walk
  }
 }

 const onMouseUp = () => {
  setIsDragging(false)
 }

 useEffect(() => {
  const refCurrent = containerRef.current
  if (refCurrent) {
   refCurrent.addEventListener("mousemove", onMouseMove)
   refCurrent.addEventListener("mouseup", onMouseUp)
   refCurrent.addEventListener("mouseleave", onMouseUp)

   return () => {
    refCurrent.removeEventListener("mousemove", onMouseMove)
    refCurrent.removeEventListener("mouseup", onMouseUp)
    refCurrent.removeEventListener("mouseleave", onMouseUp)
   }
  }
 }, [isDragging, startX, scrollStart])

 // Combined onChange handler
 const handleMissingData = () => {
  // Determine the new value of showLiveData before setting it
  const newShowLiveData = !showLiveData

  // Set the new value of showLiveData
  setShowLiveData(newShowLiveData)

  // Update the timeData state based on the new value of showLiveData
  setMissingDates((prevTimeData) => ({
   ...prevTimeData,
   // If newShowLiveData is true, update begin time, otherwise update end time
   [newShowLiveData ? "endTime" : "beginDate"]: new Date().toISOString(),
  }))
 }

 return (
  <div
   className="relative w-full  min-h-[84vh] overflow-hidden"
   style={{
    background: bodyColor,
   }}
  >
   <OutSideClickHandler callback={outSideClickHandler}>
    <SitePlanColorPicker
     isShown={isShown}
     borderColor={borderColor}
     setBorderColor={setBorderColor}
     cursorColor={cursorColor}
     setCursorColor={setCursorColor}
     bodyColor={bodyColor}
     setBodyColor={setBodyColor}
     setIsShown={setIsShown}
    />
   </OutSideClickHandler>

   <div
    style={{ background: bodyColor }}
    className="flex lg:flex-row flex-col w-full pl-2 fixed py-2.5 z-10 top-[75px] lg:top-[109px] 2xl:top-[92px] "
   >
    <div className="flex w-fit">
     <select
      className="outline-none py-2 rounded text-sm text-black "
      value={minutesSelect}
      onChange={handleSelectChange}
     >
      <option value="">Select Minutes</option>
      {options.map((option) => (
       <option key={option.value} value={option.value}>
        {option.label}
       </option>
      ))}
     </select>

     <div className="w-full mx-2">
      <Button
      className="px-6"
       size={"md"}
       onClick={() => {
        handlePrint()
       }}
      >
       Print
      </Button>
     </div>
     <div
      className="bg-white rounded cursor-pointer  w-full flex justify-center px-4"
      onClick={() => setIsShown(!isShown)}
     >
      <svg
       xmlns="http://www.w3.org/2000/svg"
       width="50"
       height="35"
       fill="#000"
       version="1.1"
       viewBox="0 0 507.745 507.745"
       xmlSpace="preserve"
      >
       <g>
        <path
         fill="#FFC52F"
         d="M211.681 303.905c-.4-4-4-7.6-8.4-7.6h-12c-.8 0-1.6-.4-2-1.6-3.2-12-9.2-23.2-17.6-32.8-.4-.8-.8-1.6 0-2.4l6.8-10c2.4-3.2 1.6-8.4-1.6-11.2l-4.8-4c-3.2-2.4-8.4-2.4-11.2.4l-8.4 8.8c-.8.8-1.6.8-2.4.4-10.8-6.4-22.8-10-35.2-10.8-.8 0-1.6-.8-2-1.6l-2-12c-.8-4-4.8-7.2-8.8-6.8l-6 .4c-4 .4-7.6 4-7.6 8.4v12.4c0 .8-.4 1.6-1.6 2-12 3.2-23.2 9.2-32.8 17.2-.8.4-1.6.8-2.4 0l-10-6.8c-3.2-2.4-8.4-1.6-11.2 1.6l-4 4.8c-2.8 3.2-2.4 8.4.4 11.2l8.8 8.4c.8.8.8 1.6.4 2.4-6.4 10.8-10 23.2-10.8 35.6 0 .8-.8 1.6-1.6 2l-11.6 2c-4 .8-7.2 4.8-6.8 8.8l.4 6c.4 4 4 7.6 8 7.6h12c.8 0 1.6.4 2 1.6 3.2 12 9.2 23.6 17.6 33.2.4.8.8 1.6 0 2.4l-6.8 9.6c-2.4 3.6-1.6 8.4 1.6 11.2l4.8 4c3.2 2.4 8.4 2.4 11.2-.4l8-8.4c.4-.4.8-.4 1.6-.4.4 0 .8 0 .8.4 11.2 6.4 23.2 10 36 11.2.8 0 1.6.8 2 1.6l2 11.2c.8 4 4.8 7.2 8.8 6.8l6-.4c4-.4 7.6-4 7.6-8.4v-11.6c0-.8.4-1.6 1.6-2 12.4-3.2 23.6-9.2 33.2-17.6.8-.4 1.6-.8 2.4 0l9.6 6.8c3.2 2.4 8.4 1.6 11.2-1.6l4-4.8c2.8-3.2 2.4-8.4-.8-11.2l-8.4-8c-.8-.8-.8-1.6-.4-2.4 6.4-10.8 10-23.2 10.8-35.6 0-.8.8-1.6 1.6-2l11.6-2c4-.8 7.2-4.8 6.8-8.8l-.4-7.2zm-142 16.8c-2-22 14-41.6 36-43.6h4c20.8 0 37.6 15.6 39.6 36 1.2 10.8-2 21.2-8.8 29.2-6.8 8.4-16.4 13.2-27.2 14.4-22.4 2.4-41.6-14-43.6-36z"
        ></path>
        <path
         fill="#61C2AB"
         d="M501.681 214.305c-.4-6-6-12-12-12l-16.8-.4c-.8 0-1.6 0-2-.8-4.4-16.8-13.2-32.4-24.4-45.2-.4-.8-.8-1.6 0-2.4l9.6-13.6c3.6-5.2 2.4-12.4-2.4-16.4l-6.4-5.2c-4.4-3.6-12.4-3.2-16.4.8l-12 12c-.8.8-1.6.8-2.4.4-15.2-8.8-32-14-49.6-15.2-.8 0-1.6-.8-2-1.6l-2.8-16.8c-1.2-6-7.2-10.4-13.2-10l-8.4.8c-6 .4-11.2 6-11.2 12.4l.4 17.2c0 .8-.4 1.6-1.6 2-16.8 4.4-32.4 12.8-45.6 24-.8.4-1.6.8-2.4 0l-14-9.6c-4.8-3.2-12.8-2.4-16.4 2.4l-5.2 6.4c-4 4.8-3.6 12.4.8 16.4l12 11.6c.8.8.8 1.6.4 2.4-8.8 15.2-14 32.4-15.2 49.6 0 .8-.8 1.6-1.6 2l-16.4 2.8c-6 1.2-10.4 7.2-10 13.2l.8 8.4c.4 6 6 9.6 12 9.6h16.8c.8 0 1.6.4 1.6 1.6 4.4 16.8 12.8 33.6 24.4 47.2.4.8.8 2 0 2.8l-9.2 13.6c-3.6 5.2-2.4 12.4 2.4 16.4l6.4 5.2c4.4 3.6 12.4 3.2 16.4-.8l11.2-11.6c.4-.4.8-.4 1.6-.4.4 0 .8 0 1.2.4 15.2 8.8 32.8 14.4 50 15.6.8 0 1.6.8 2 1.6l2.8 15.6c1.2 6 7.2 10.4 13.2 10l8.4-.8c6-.4 11.2-6 11.2-12.4l-.4-16c0-.8.4-1.6 1.6-2 17.2-4.4 32.8-12.8 46.4-24.8.8-.4 1.6-.8 2.4 0l13.2 9.2c4.8 3.6 12.8 2.4 16.4-2.4l5.2-6.4c4-4.8 3.6-12-.8-16.4l-11.6-11.2c-.8-.8-.8-1.6-.4-2.4 8.8-15.2 14-32.4 15.2-50 0-.8.8-1.6 1.6-2l16-2.8c6-1.2 10.8-7.2 10-13.2l-.8-8.8zm-79.2-22c10.8 17.2 14.4 37.2 10 57.2-4.4 19.6-16.4 36.4-33.2 47.2-17.2 10.8-37.2 14.4-57.2 10s-36.4-16.4-47.2-33.2c-10.8-17.2-14.4-37.2-10-57.2 4.4-19.6 16.4-36.4 33.2-47.2 20.4-13.2 46.4-15.2 69.2-6.4 1.6.8 3.2 1.2 4.8 2 .4 0 10 5.2 17.2 12 7.2 6.8 12.8 15.2 13.2 15.6z"
        ></path>
        <path d="M109.281 363.105c-25.2 0-46-20.8-46-46s20.8-46 46-46 46 20.8 46 46-20.4 46-46 46zm0-84c-20.8 0-38 17.2-38 38s17.2 38 38 38 38-17.2 38-38c0-21.2-16.8-38-38-38z"></path>
        <path d="M115.681 425.505c-6.8 0-12.8-5.2-14-11.6l-1.6-8.4c-11.6-1.2-22.4-4.4-32.4-10l-6 6c-5.2 5.2-13.6 5.6-19.2 1.2l-4.8-4c-5.6-4.4-6.8-13.2-2.8-19.2l4.8-7.2c-7.2-8.8-12.4-19.2-16-30h-8.8c-7.2 0-13.6-5.6-14.4-12.8l-.4-6c-.8-7.2 4.4-14 11.6-15.2l8.8-1.6c1.2-11.2 4.8-22 10-32l-6.4-6.4c-5.2-5.2-5.6-13.6-1.2-19.2l4-4.8c4.4-5.6 13.2-6.8 19.2-2.8l7.6 5.2c8.8-7.2 18.8-12.4 29.6-15.6v-9.2c0-7.2 5.6-13.6 12.8-14.4l6-.4c7.2-.8 14 4.4 15.2 11.6l1.6 9.2c11.2 1.2 22 4.4 32 9.6l6.4-6.4c5.2-5.2 13.6-5.6 19.2-1.2l4.8 3.6c5.6 4.4 6.8 13.2 2.8 19.2l-5.2 7.6c7.2 8.8 12.4 18.8 16 29.6h8.8c7.2 0 13.6 5.6 14.4 12.8l.4 6c.8 7.2-4.4 14-11.6 15.2l-8.4 1.6c-1.2 11.2-4.4 22.4-10 32.4l6 6c5.2 5.2 5.6 13.6 1.2 19.2l-4 4.8c-4.4 5.6-13.2 6.8-19.2 2.8l-6.8-4.8c-8.8 7.2-19.2 12.8-30 16v8.4c0 7.2-5.6 13.6-12.8 14.4l-6 .4c-.4.4-.8.4-1.2.4zm-48.8-38.8c.8 0 1.2 0 2 .4 10.8 6.4 22.4 10 34.8 10.8 2 0 3.2 1.6 3.6 3.2l2 11.2c.4 3.2 3.6 5.2 6.8 5.2l6-.4c3.2-.4 5.6-3.2 5.6-6.4v-11.6c0-2 1.2-3.6 2.8-4 12-3.2 22.8-8.8 32.4-17.2 1.2-1.2 3.6-1.2 4.8-.4l9.6 6.8c2.4 1.6 6.4 1.2 8.4-1.2l4-4.8c2-2.4 1.6-6.4-.4-8.4l-8.4-8c-1.2-1.2-1.6-3.2-.8-4.8 6-10.8 9.6-22.8 10.4-34.8 0-2 1.6-3.2 3.2-3.6l11.6-2c3.2-.4 5.2-3.6 5.2-6.8l-.4-6c-.4-3.2-3.2-5.6-6.4-5.6h-12c-2 0-3.6-1.2-4-2.8-3.2-12-8.8-22.4-17.2-32-1.2-1.2-1.2-3.6-.4-4.8l6.8-10c1.6-2.4 1.2-6.4-1.2-8.4l-4.8-3.6c-2.4-2-6.4-1.6-8.4.4l-8.4 8.8c-1.2 1.2-3.2 1.6-4.8.8-10.8-6-22.4-9.6-34.4-10.4-2 0-3.2-1.6-3.6-3.2l-2-12c-.4-3.2-3.6-5.6-6.8-5.2l-6 .4c-3.2.4-5.6 3.2-5.6 6.4v12.4c0 2-1.2 3.6-2.8 4-12 3.2-22.4 8.8-32 16.8-1.2 1.2-3.2 1.2-4.8.4l-10-6.8c-2.4-1.6-6.4-1.2-8.4 1.2l-4 4.8c-2 2.4-1.6 6.4.4 8.4l8.8 8.4c1.2 1.2 1.6 3.2.8 4.8-6 10.8-10 22.4-10.8 34.4 0 2-1.6 3.2-3.2 3.6l-11.6 2c-3.2.4-5.2 3.6-5.2 6.8l.4 6c.4 3.2 3.2 5.6 6.4 5.6h12c2 0 3.6 1.2 4 2.8 3.2 12 8.8 22.8 16.8 32.4 1.2 1.2 1.2 3.6.4 4.8l-6.8 9.6c-1.6 2.4-1.2 6.4 1.2 8.4l4.8 4c2.4 2 6.4 1.6 8.4-.4l8-8.4c.8-1.6 2-2 3.2-2zM358.481 314.305c-6 0-12-.8-18-2-21.2-4.8-39.6-17.6-51.2-36-11.6-18.4-15.6-40.4-10.8-61.6s17.6-39.6 36-51.2c22.4-14 50-16.4 74.4-6.8 1.6.8 3.6 1.6 5.2 2.4 2 .8 2.8 3.2 2 5.2s-3.2 2.8-5.2 2c-1.6-.8-3.2-1.6-4.8-2-22-8.8-47.2-6.8-67.2 6-16.8 10.4-28 26.8-32.4 46s-.8 38.8 9.6 55.6c10.4 16.8 26.8 28 46 32.4s38.8.8 55.6-9.6 28-26.8 32.4-46 .8-38.8-9.6-55.6c-1.2-2-.8-4.4 1.2-5.6s4.4-.8 5.6 1.2c11.6 18.4 15.6 40.4 10.8 61.6s-17.6 39.6-36 51.2c-13.2 8.4-28 12.8-43.6 12.8z"></path>
        <path d="M366.881 382.305c-8.4 0-16.4-6.4-18-14.8l-2.4-12.8c-16.4-1.6-32.4-6.4-46.8-14.4l-9.2 9.2c-6.4 6.8-17.6 7.2-24.8 1.2l-6.4-5.2c-7.2-6-8.8-16.8-3.2-24.4l7.6-10.8c-10.4-12.8-18.4-27.6-22.8-43.2h-13.2-.4c-9.2 0-17.2-7.2-18-16.4l-.8-8.4c-.8-9.2 6-18 14.8-19.6l13.2-2.4c1.6-16 6.4-32 14.4-46.4l-10-9.6c-6.8-6.4-7.2-17.6-1.2-24.8l5.2-6.4c6-7.2 16.8-8.8 24.4-3.2l11.2 8c12.8-10.4 27.2-18 42.8-22.4v-14c0-9.2 7.2-17.6 16.4-18.4l8.4-.8c9.2-.8 18 6 19.6 14.8l2.4 13.6c16.4 1.6 31.6 6.4 46 14l9.6-10c6.4-6.8 17.6-7.2 24.8-1.2l6.4 5.2c7.2 6 8.8 16.8 3.2 24.4l-8 11.2c10.4 12.4 18.4 27.2 22.8 42.8h14c9.2 0 17.2 7.2 18 16.4l.8 8.4c.8 9.2-6 18-14.8 19.6l-13.2 2.4c-1.6 16.4-6.4 32.4-14 46.8l9.6 9.2c6.8 6.4 7.2 17.6 1.2 24.8l-5.2 6.4c-6 7.2-16.8 8.8-24.4 3.2l-10.8-7.6c-12.8 10.8-27.6 18.4-43.2 23.2v12.8c0 9.2-7.2 17.6-16.4 18.4l-8.4.8c0 .4-.4.4-1.2.4zm-68-51.2c.8 0 1.2 0 2 .4 15.2 8.8 32 14 49.2 15.2 2 0 3.2 1.6 3.6 3.2l2.8 15.6c.8 5.2 6 8.8 11.2 8.4l8.4-.8c5.2-.4 9.2-5.2 9.2-10.4l-.4-16c0-2 1.2-3.6 2.8-4 16.8-4.4 32.4-12.8 45.6-24.4 1.2-1.2 3.6-1.2 4.8-.4l13.2 9.2c4 2.8 10.4 2 13.6-2l5.2-6.4c3.2-4 2.8-10.4-.8-14l-11.6-11.2c-1.2-1.2-1.6-3.2-.8-4.8 8.8-14.8 13.6-32 14.8-49.2 0-2 1.6-3.2 3.2-3.6l16-2.8c5.2-.8 8.8-6 8.4-11.2l-.8-8.4c-.4-5.2-5.2-9.2-10.4-9.2l-16.4.4c-2 0-3.6-1.2-4-2.8-4.4-16.8-12.8-32-24-44.8-1.2-1.2-1.2-3.6-.4-4.8l9.6-13.6c2.8-4 2-10.4-2-13.6l-6.4-5.2c-4-3.2-10.4-2.8-14 .8l-12 12c-1.2 1.2-3.2 1.6-4.8.8-15.2-8.8-31.2-13.6-48.4-14.8-2 0-3.2-1.6-3.6-3.2l-2.8-16.8c-.8-5.2-6-8.8-11.2-8.4l-8.4.8c-5.2.4-9.2 5.2-9.2 10.4l.4 16.8c0 2-1.2 3.6-2.8 4-16.8 4.4-31.6 12.4-44.8 23.6-1.2 1.2-3.6 1.2-4.8.4l-14-9.6c-4-2.8-10.4-2-13.6 2l-5.2 6.4c-3.2 4-2.8 10.4.8 13.6l12 11.6c1.2 1.2 1.6 3.2.8 4.8-8.8 14.8-14 31.6-15.2 48.8 0 2-1.6 3.2-3.2 3.6l-16.4 2.8c-5.2.8-8.8 6-8.4 11.2l.8 8.4c.4 5.2 5.2 9.2 10.4 9.2l16.4-.4c2 0 3.6 1.2 4 2.8 4.4 16.8 12.8 32.4 24 45.6 1.2 1.2 1.2 3.2.4 4.8l-9.2 13.2c-2.8 4-2 10.4 2 13.6l6.4 5.2c4 3.2 10.4 2.8 14-.8l11.2-11.6c.8 0 2-.4 2.8-.4z"></path>
       </g>
      </svg>
     </div>
    </div>
    <div className="w-full flex lg:py-0 py-2">
     <label className="flex cursor-pointer select-none items-center text-black px-2">
      <div className="relative mr-3">
       <input
        type="checkbox"
        checked={showLiveData}
        onChange={handleMissingData}
        className="sr-only"
       />
       <div
        className={`block h-8 w-14 rounded-full ${showLiveData ? "bg-golden" : "bg-gray"}`}
       ></div>
       <div
        className={`dot absolute left-1 top-1 h-6 w-6 rounded-full bg-white ${
         showLiveData ? "translate-x-6" : ""
        } transition`}
       ></div>
      </div>
      Live Data
     </label>
     <label className="flex cursor-pointer select-none items-center px-2 text-black">
      <div className="relative mr-3">
       <input
        type="checkbox"
        checked={isScroll}
        onChange={() => {
         setIsScroll(!isScroll)
        }}
        className="sr-only"
       />
       <div className={`block h-8 w-14 rounded-full ${isScroll ? "bg-golden" : "bg-gray"}`}></div>
       <div
        className={`dot absolute left-1 top-1 h-6 w-6 rounded-full bg-white ${
         isScroll ? "translate-x-6" : ""
        } transition`}
       ></div>
      </div>
      Scroll
     </label>
    </div>
   </div>

   <div className="example-container">
    <Tooltip
     anchorSelect=".tooltip-element"
     place="bottom"
     className="example-orange z-50"
     opacity={0.9}
    >
     <div className="flex flex-col">
      <div className="flex">
       <h1>Date:</h1>
       <div className="ml-2">
        {informationData &&
         new Date(informationData.entry_date).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
         })}
       </div>
      </div>

      <div className=" flex">
       <h1>Time:</h1>
       <div className="ml-2">
        {informationData &&
         new Date(informationData.entry_date).toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false, // Use 24-hour format
         })}
       </div>
      </div>
     </div>

     {informationData &&
      informationData?.ap_values.map((value: any, index: number) => (
       <div key={index} className="flex justify-between">
        <div className=" flex flex-col">
         {index === 0 && "Parameter"}
         <div className="">{value.parameter}</div>
        </div>
        <div className="flex flex-col">
         {index === 0 && "Value"}
         <div className=" text-right">{value.value}</div>
        </div>
       </div>
      ))}
    </Tooltip>
   </div>
   <div style={{ background: bodyColor }} className="text-black">
    <div ref={componentRef} className="flex mt-[75px] gap-3 py-2">
     <div style={{ background: bodyColor }} className="px-2 text-black ">
      <div className={`flex whitespace-nowrap z-30 `}>
       <div className={`flex text-xs h-4 `}>
        <div className="text-xs">Date</div>
       </div>
      </div>
      <div className={`flex whitespace-nowrap z-30`}>
       <div className={`flex text-xs  h-4 `}>
        <div className="text-xs">Zeil</div>
       </div>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
       <Droppable droppableId="droppable-site-plan">
        {(provided) => (
         <div {...provided.droppableProps} ref={provided.innerRef} className="">
          {allData?.minutesSignalGroupDetectorsTXValue &&
           allData?.minutesSignalGroupDetectorsTXValue?.map((element: any, index: number) => {
            return (
             <Draggable key={`draggable-${index}`} draggableId={`draggable-${index}`} index={index}>
              {(provided) => (
               <div ref={provided.innerRef} {...provided.draggableProps}>
                {element.length > 0 && (
                 <div className={`flex whitespace-nowrap z-30 `} {...provided.dragHandleProps}>
                  {/* Apply dragHandleProps only to the label section */}
                  <div key={index} className={`flex text-xs h-4`}>
                   <div>
                    {element[0]?.type === "min"
                     ? "TX"
                     : element[0]?.type === "signalGroup"
                       ? "Ocit Number"
                       : element[0]?.type === "detector"
                         ? "Detector"
                         : element[0]?.type === "signalPlan"
                           ? "Signal Plan"
                           : ""}
                   </div>{" "}
                   {element[0]?.ocit_outstation_number && (
                    <div className="pl-2 text-xs">{element[0].ocit_outstation_number}</div>
                   )}
                  </div>
                 </div>
                )}
               </div>
              )}
             </Draggable>
            )
           })}
          {provided.placeholder}
         </div>
        )}
       </Droppable>
      </DragDropContext>
     </div>

     <div
      style={{ background: bodyColor }}
      ref={containerRef}
      onMouseDown={onMouseDown}
      className={`overflow-x-auto border-zinc-400  border-l pl-2 text-black   ${
       isDragging && "cursor-move"
      }`}
     >
      {/* date */}
      <div className="flex">
       {allData?.dates?.map((date: any, index) => {
        const display =
         index === 0 ||
         new Date(date).toLocaleDateString() !==
          new Date(allData?.dates[index - 1]).toLocaleDateString()
          ? true
          : false

        return (
         <div key={index} className=" text-sm h-4">
          <div>{display && new Date(date).toLocaleDateString()}</div>
         </div>
        )
       })}
      </div>
      {/* Zeil */}
      <div className="flex">
       {allData?.dates?.map((date: any, index) => {
        const display =
         index === 0 ||
         new Date(date).toLocaleDateString() +
          "-" +
          new Date(date).toLocaleTimeString("en-GB", {
           hour: "2-digit",
           minute: "2-digit",
           timeZone: "UTC",
          }) !==
          new Date(allData?.dates[index - 1]).toLocaleDateString() +
           "-" +
           new Date(allData?.dates[index - 1]).toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "UTC",
           })
          ? true
          : false

        const differenceInSeconds: number =
         (new Date(date).getTime() - new Date(allData?.dates?.[index - 1]).getTime()) / 1000
        return (
         <div
          style={{ width: `${differenceInSeconds * 24}px` }}
          key={index}
          className=" text-sm h-4 "
         >
          {display && minuteHandler(new Date(date).toISOString(), minutesSelect)}
         </div>
        )
       })}
      </div>
      {allData?.minutesSignalGroupDetectorsTXValue &&
       calculateWidthHanlder(allData?.minutesSignalGroupDetectorsTXValue).map((element, index) => (
        <GridLine
         key={index}
         element={element}
         hoverIndex={hoverIndex}
         processAllData={processAllData}
         cursorColor={cursorColor}
        />
       ))}
     </div>
    </div>
   </div>
  </div>
 )
}
